/**
 * Route wrapper that gates on auth + optional RBAC capability.
 *
 * Behavior:
 *   * While `isLoading` (session restore in flight), renders the spinner.
 *   * If unauthenticated, redirects to `/login` — the current pathname is
 *     stashed in `location.state.from` so LoginPage can bounce back after
 *     a successful sign-in.
 *   * If authenticated but the required `cap` isn't in the user's set,
 *     redirects to `/` (the map, which every role can see).
 *   * Otherwise renders `children`.
 *
 * Extracted from App.tsx (2026-07-14) so the gate can be unit-tested in
 * isolation without pulling every route/page into the test bundle.
 */

import React from "react";
import { Navigate, useLocation } from "react-router-dom";

import LoadingSpinner from "./LoadingSpinner";
import { useAuth } from "../contexts/AuthContext";

export interface ProtectedRouteProps {
  children: React.ReactNode;
  /** When set, only users holding this RBAC capability may view the route;
   *  others bounce to "/". */
  cap?: string;
  /** When set, only these roles may view the route; others bounce to "/".
   *  Use for genuinely platform-only pages (e.g. SOPs) that no single
   *  capability models — `cap` alone can't express "platform, not a Customer
   *  Admin who happens to hold users.manage". */
  roles?: string[];
}

const ProtectedRoute: React.FC<ProtectedRouteProps> = ({ children, cap, roles }) => {
  const { isAuthenticated, isLoading, can, role } = useAuth();
  const location = useLocation();
  if (isLoading) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <LoadingSpinner />
      </div>
    );
  }
  if (!isAuthenticated) {
    return <Navigate to="/login" replace state={{ from: location.pathname }} />;
  }
  if (cap && !can(cap)) {
    return <Navigate to="/" replace />;
  }
  if (roles && (!role || !roles.includes(role))) {
    return <Navigate to="/" replace />;
  }
  return <>{children}</>;
};

export default ProtectedRoute;
